import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import type { Editor } from '@tiptap/react';
import { useTranslation } from '@tyro/i18n';
import {
  InsertColumnAfterIcon,
  InsertColumnBeforeIcon,
  InsertRowAfterIcon,
  InsertRowBeforeIcon,
  RemoveColumnIcon,
  RemoveRowIcon,
  RemoveTableIcon,
} from '@tyro/icons';
import type { Dispatch, SetStateAction } from 'react';

export type TableMenuPosition = {
  mouseX: number;
  mouseY: number;
  pos: number; // This is the ProseMirror position of the clicked cell
} | null;

type TableCommand =
  | 'addColumnAfter'
  | 'addColumnBefore'
  | 'addRowAfter'
  | 'addRowBefore'
  | 'deleteRow'
  | 'deleteColumn'
  | 'deleteTable';

type TableMenuProps = {
  editor: Editor | null;
  tableMenu: TableMenuPosition;
  setTableMenu: Dispatch<SetStateAction<TableMenuPosition>>;
};

export function TableMenu({ editor, tableMenu, setTableMenu }: TableMenuProps) {
  const { t } = useTranslation(['common']);
  const { palette } = useTheme();
  const executeTableCommand = (command: TableCommand) => {
    if (!editor) return;

    editor.chain().focus()[command]().run();
    handleTableMenuClose();
  };

  const handleTableMenuClose = () => {
    setTableMenu(null);
  };

  return (
    <Menu
      open={tableMenu !== null}
      onClose={handleTableMenuClose}
      anchorReference="anchorPosition"
      anchorPosition={
        tableMenu
          ? { top: tableMenu.mouseY, left: tableMenu.mouseX }
          : undefined
      }
      slotProps={{
        list: {
          dense: true,
        },
      }}
      sx={{
        '& .MuiMenuItem-root .MuiListItemIcon-root': {
          minWidth: 'auto',
        },
      }}
    >
      {[
        {
          icon: <InsertRowBeforeIcon />,
          action: () => executeTableCommand('addRowBefore'),
          text: t('common:tooltipTitles.insertRowBefore'),
        },
        {
          icon: <InsertRowAfterIcon />,
          action: () => executeTableCommand('addRowAfter'),
          text: t('common:tooltipTitles.insertRowAfter'),
        },
        <Divider key="d-1" />,
        {
          icon: <InsertColumnBeforeIcon />,
          action: () => executeTableCommand('addColumnBefore'),
          text: t('common:tooltipTitles.insertColumnBefore'),
        },
        {
          icon: <InsertColumnAfterIcon />,
          action: () => executeTableCommand('addColumnAfter'),
          text: t('common:tooltipTitles.insertColumnAfter'),
        },
        <Divider key="d-2" />,
        {
          icon: <RemoveRowIcon />,
          action: () => executeTableCommand('deleteRow'),
          text: t('common:tooltipTitles.deleteRow'),
          isDelete: true,
        },
        {
          icon: <RemoveColumnIcon />,
          action: () => executeTableCommand('deleteColumn'),
          text: t('common:tooltipTitles.deleteColumn'),
          isDelete: true,
        },
        {
          icon: <RemoveTableIcon />,
          action: () => executeTableCommand('deleteTable'),
          text: t('common:tooltipTitles.deleteTable'),
          isDelete: true,
        },
      ].map((menuItem) => {
        if (!('icon' in menuItem)) {
          return menuItem;
        }

        return (
          <MenuItem
            key={menuItem.text}
            onClick={menuItem.action}
            sx={
              menuItem.isDelete
                ? {
                    '&.Mui-selected': {
                      backgroundColor: palette.rose[500_16],
                    },
                    '&.Mui-selected:hover, &:hover, &.Mui-focusVisible, &:focus':
                      {
                        backgroundColor: palette.rose[500_8],
                        color: palette.rose[600],
                        '& span': {
                          color: palette.rose[600],
                        },
                      },
                    '& .MuiTouchRipple-child': {
                      backgroundColor: palette.rose[500],
                    },
                  }
                : {}
            }
          >
            <ListItemIcon>{menuItem.icon}</ListItemIcon>
            <ListItemText>{menuItem.text}</ListItemText>
          </MenuItem>
        );
      })}
    </Menu>
  );
}
